"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {IconNameExtended} from "../library/Icon";
import JumpInCard from "./JumpInCard";

type CardCategory = "agent" | "gpt";

interface JumpInCardConfig {
   translationKey: string;
   icon: IconNameExtended;
   href?: string; // Direct link for feature cards
   isGailGptAction?: boolean; // If true, uses input translation key
   category: CardCategory;
}

// Gail Agent cards - feature links for agent-specific actions
const AGENT_CARDS: JumpInCardConfig[] = [
   {
      translationKey: "setupReceptionist",
      icon: "phone-volume",
      href: "/inbound/scripts",
      category: "agent",
   },
   {
      translationKey: "launchCampaign",
      icon: "paper-plane",
      href: "/outbound/campaigns",
      category: "agent",
   },
   {
      translationKey: "contactRenewals",
      icon: "arrows-rotate",
      href: "/outbound/campaigns",
      category: "agent",
   },
   {
      translationKey: "qualifyLeads",
      icon: "users",
      href: "/outbound/campaigns",
      category: "agent",
   },
];

// GailGPT cards - GPT actions that redirect to chat
const GPT_CARDS: JumpInCardConfig[] = [
   {
      translationKey: "compareCoverages",
      icon: "file-lines",
      isGailGptAction: true,
      category: "gpt",
   },
   {
      translationKey: "completeForms",
      icon: "clipboard-list",
      isGailGptAction: true,
      category: "gpt",
   },
   {
      translationKey: "reviewClaims",
      icon: "magnifying-glass",
      isGailGptAction: true,
      category: "gpt",
   },
   {
      translationKey: "buildPolicies",
      icon: "file-pen",
      isGailGptAction: true,
      category: "gpt",
   },
];

// All cards for backward compatibility (original order)
const ALL_CARDS: JumpInCardConfig[] = [...GPT_CARDS, ...AGENT_CARDS];

type Variant = "agent" | "gpt" | "all";

interface JumpInWithGailSectionProps {
   maxCards?: number;
   variant?: Variant;
   title?: string;
}

export default function JumpInWithGailSection({
   maxCards,
   variant = "all",
   title,
}: Readonly<JumpInWithGailSectionProps>) {
   const {t} = useTranslation("stats");

   const getCardHref = (card: JumpInCardConfig): string => {
      if (card.href) {
         return card.href;
      }
      if (card.isGailGptAction) {
         return `/gailgpt?input=${encodeURIComponent(
            t(`emptyState.jumpInWithGail.cards.${card.translationKey}.input`)
         )}`;
      }
      return "/gailgpt";
   };

   // Select cards based on variant
   const getCards = (): JumpInCardConfig[] => {
      switch (variant) {
         case "agent":
            return AGENT_CARDS;
         case "gpt":
            return GPT_CARDS;
         case "all":
         default:
            return ALL_CARDS;
      }
   };

   const cards = getCards();
   const displayCards = maxCards ? cards.slice(0, maxCards) : cards;

   // Determine section title
   const getSectionTitle = (): string => {
      if (title) return title;
      switch (variant) {
         case "agent":
            return t("emptyState.quickstart.gailAgent");
         case "gpt":
            return t("emptyState.quickstart.gailGpt");
         case "all":
         default:
            return t("emptyState.jumpInWithGail.title");
      }
   };

   return (
      <div className={clsx("flex", "flex-col", "gap-4")}>
         <h2 className={clsx("text-900", "font-medium")}>{getSectionTitle()}</h2>
         <div
            className={clsx(
               "grid",
               "grid-cols-1",
               "sm:grid-cols-2",
               // Use 4 columns for variant sections, 2 columns for compact/empty state
               {"lg:grid-cols-4": variant !== "all" || !maxCards},
               "gap-4"
            )}
         >
            {displayCards.map((card) => (
               <JumpInCard
                  key={card.translationKey}
                  icon={card.icon}
                  title={t(`emptyState.jumpInWithGail.cards.${card.translationKey}.title`)}
                  description={t(
                     `emptyState.jumpInWithGail.cards.${card.translationKey}.description`
                  )}
                  href={getCardHref(card)}
                  iconClassName={card.category === "agent" ? "text-green-500" : "text-blue-500"}
                  iconBgClassName={card.category === "agent" ? "bg-green-500/10" : "bg-blue-500/10"}
                  hoverBorderClassName={
                     card.category === "agent" ? "hover:border-green-500" : "hover:border-blue-500"
                  }
               />
            ))}
         </div>
      </div>
   );
}
